import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, Send, EyeOff, Layers } from "lucide-react";
import { combineAmericanOdds, todayBettingDate, formatOdds } from "@/lib/parlay";

const RISKS = ["low", "medium", "high"] as const;
const STATUSES = ["draft", "approved", "published", "rejected"] as const;

type Sport = { id: string; code: string };

type LegInput = {
  id?: string;
  leg_order: number;
  sport_id: string;
  game_description: string;
  pick_description: string;
  line: string;
  odds: string;
  reasoning: string;
  commence_time: string; // datetime-local string
};

type ParlayForm = {
  id?: string;
  betting_date: string;
  title: string;
  suggested_units: string;
  risk: string;
  ai_summary: string;
  correlation_notes: string;
  status: string;
  legs: LegInput[];
};

const newLeg = (order: number): LegInput => ({
  leg_order: order, sport_id: "", game_description: "", pick_description: "",
  line: "", odds: "", reasoning: "", commence_time: "",
});

// Convert ISO timestamp -> "YYYY-MM-DDTHH:mm" in user's local tz for <input type="datetime-local">
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const emptyForm = (): ParlayForm => ({
  betting_date: todayBettingDate(),
  title: "",
  suggested_units: "0.5",
  risk: "high",
  ai_summary: "",
  correlation_notes: "",
  status: "draft",
  legs: [newLeg(0), newLeg(1)],
});

export default function ParlayOfDayManager({ sports }: { sports: Sport[] }) {
  const [parlays, setParlays] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<ParlayForm>(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("parlay_of_day")
      .select("*")
      .order("betting_date", { ascending: false })
      .limit(60);
    if (error) toast.error(error.message);
    setParlays(data ?? []);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  function openCreate() { setForm(emptyForm()); setOpen(true); }

  async function openEdit(p: any) {
    const { data: legs } = await supabase
      .from("parlay_of_day_legs").select("*").eq("parlay_id", p.id).order("leg_order");
    setForm({
      id: p.id,
      betting_date: p.betting_date,
      title: p.title,
      suggested_units: String(p.suggested_units),
      risk: p.risk,
      ai_summary: p.ai_summary ?? "",
      correlation_notes: p.correlation_notes ?? "",
      status: p.status,
      legs: (legs ?? []).map((l: any, i: number) => ({
        id: l.id,
        leg_order: l.leg_order ?? i,
        sport_id: l.sport_id ?? "",
        game_description: l.game_description ?? "",
        pick_description: l.pick_description ?? "",
        line: l.line != null ? String(l.line) : "",
        odds: l.odds != null ? String(l.odds) : "",
        reasoning: l.reasoning ?? "",
        commence_time: l.commence_time ? toLocalInput(l.commence_time) : "",
      })),
    });
    setOpen(true);
  }

  function setLegs(updater: (legs: LegInput[]) => LegInput[]) {
    setForm((f) => ({ ...f, legs: updater(f.legs) }));
  }

  async function save() {
    if (!form.title.trim()) return toast.error("Title is required");
    if (form.legs.length < 2 || form.legs.length > 6) return toast.error("Parlay must have 2–6 legs");
    for (const l of form.legs) {
      if (!l.game_description.trim() || !l.pick_description.trim()) return toast.error("Every leg needs a game and pick");
      if (l.odds === "" || isNaN(Number(l.odds))) return toast.error("Every leg needs valid odds");
    }
    const units = Math.min(0.5, Math.max(0.1, Number(form.suggested_units) || 0.5));
    const total = combineAmericanOdds(form.legs.map((l) => Number(l.odds)));

    const payload: any = {
      betting_date: form.betting_date,
      title: form.title,
      total_odds: total,
      suggested_units: units,
      risk: form.risk,
      ai_summary: form.ai_summary || null,
      correlation_notes: form.correlation_notes || null,
      status: form.status,
      leg_count: form.legs.length,
    };
    if (form.status === "published") payload.published_at = new Date().toISOString();

    setSaving(true);
    try {
      let parlayId = form.id;
      if (parlayId) {
        const { error } = await supabase.from("parlay_of_day").update(payload).eq("id", parlayId);
        if (error) throw error;
        await supabase.from("parlay_of_day_legs").delete().eq("parlay_id", parlayId);
      } else {
        const { data, error } = await supabase.from("parlay_of_day").insert(payload).select().single();
        if (error) throw error;
        parlayId = data!.id;
      }
      const legsPayload = form.legs.map((l, i) => ({
        parlay_id: parlayId,
        leg_order: i,
        sport_id: l.sport_id || null,
        game_description: l.game_description,
        pick_description: l.pick_description,
        line: l.line === "" ? null : Number(l.line),
        odds: Number(l.odds),
        reasoning: l.reasoning || null,
        commence_time: l.commence_time ? new Date(l.commence_time).toISOString() : null,
      }));
      const { error: legsErr } = await supabase.from("parlay_of_day_legs").insert(legsPayload);
      if (legsErr) throw legsErr;
      toast.success(form.id ? "Parlay saved" : "Parlay created");
      setOpen(false);
      load();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save parlay");
    } finally {
      setSaving(false);
    }
  }

  async function quickUpdate(id: string, patch: any) {
    const { error } = await supabase.from("parlay_of_day").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Updated");
    load();
  }

  async function settle(p: any, result: "won" | "lost" | "pushed") {
    const units = Number(p.suggested_units || 0);
    const dec = p.total_odds ? (p.total_odds > 0 ? p.total_odds / 100 : 100 / Math.abs(p.total_odds)) : 0;
    const delta = result === "won" ? units * dec : result === "lost" ? -units : 0;
    quickUpdate(p.id, { result, units_delta: delta });
  }

  async function remove(id: string) {
    if (!confirm("Delete this parlay?")) return;
    const { error } = await supabase.from("parlay_of_day").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  }

  return (
    <div className="te-card overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Layers className="w-5 h-5 text-accent" /> Parlay of the Day
        </h2>
        <Button size="sm" onClick={openCreate}><Plus className="w-3 h-3 mr-1" /> New Parlay</Button>
      </div>

      <div className="divide-y divide-border">
        {loading && <div className="p-6 text-center text-muted-foreground">Loading…</div>}
        {!loading && parlays.length === 0 && <div className="p-6 text-center text-muted-foreground">No parlays yet.</div>}
        {parlays.map((p) => (
          <div key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium truncate">{p.title}</div>
              <div className="text-xs text-muted-foreground">
                {p.betting_date} · {p.leg_count} legs · {formatOdds(p.total_odds)} · {p.suggested_units}u · <span className="capitalize">{p.risk}</span> risk
              </div>
            </div>
            <div className="flex flex-wrap gap-1 items-center">
              <span className="te-chip bg-secondary border border-border capitalize text-xs">{p.status}</span>
              <span className="te-chip bg-secondary border border-border capitalize text-xs">{p.result}</span>
              <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
              {p.status === "draft" && (
                <>
                  <Button size="sm" variant="secondary" onClick={() => quickUpdate(p.id, { status: "approved" })}><Check className="w-3 h-3 mr-1" />Approve</Button>
                  <Button size="sm" variant="ghost" onClick={() => quickUpdate(p.id, { status: "rejected" })}><X className="w-3 h-3 mr-1" />Reject</Button>
                </>
              )}
              {p.status === "approved" && (
                <Button size="sm" onClick={() => quickUpdate(p.id, { status: "published", published_at: new Date().toISOString() })}>
                  <Send className="w-3 h-3 mr-1" />Publish
                </Button>
              )}
              {p.status === "published" && (
                <Button size="sm" variant="ghost" onClick={() => quickUpdate(p.id, { status: "approved", published_at: null })}>
                  <EyeOff className="w-3 h-3 mr-1" />Unpublish
                </Button>
              )}
              {p.status === "published" && p.result === "pending" && (
                <>
                  <Button size="sm" variant="secondary" onClick={() => settle(p, "won")}>Won</Button>
                  <Button size="sm" variant="secondary" onClick={() => settle(p, "lost")}>Lost</Button>
                  <Button size="sm" variant="secondary" onClick={() => settle(p, "pushed")}>Push</Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={() => remove(p.id)}><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Edit Parlay" : "Create Parlay of the Day"}</DialogTitle></DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Betting Date">
              <Input type="date" value={form.betting_date} onChange={(e) => setForm((f) => ({ ...f, betting_date: e.target.value }))} />
            </Field>
            <Field label="Title">
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Tuesday Night 4-Leg" />
            </Field>
            <Field label="Suggested Units (max 0.5)">
              <Input type="number" min={0.1} max={0.5} step={0.1} value={form.suggested_units}
                onChange={(e) => setForm((f) => ({ ...f, suggested_units: e.target.value }))} />
            </Field>
            <Field label="Risk">
              <Select value={form.risk} onValueChange={(v) => setForm((f) => ({ ...f, risk: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{RISKS.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="AI Summary" full>
              <Textarea rows={2} value={form.ai_summary} onChange={(e) => setForm((f) => ({ ...f, ai_summary: e.target.value }))} />
            </Field>
            <Field label="Correlation Notes" full>
              <Textarea rows={2} value={form.correlation_notes}
                onChange={(e) => setForm((f) => ({ ...f, correlation_notes: e.target.value }))}
                placeholder="Note any correlations between legs (e.g. both depend on a high total)" />
            </Field>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Legs ({form.legs.length}/6)</Label>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                Combined odds: <span className="font-semibold text-foreground">
                  {formatOdds(combineAmericanOdds(form.legs.map((l) => Number(l.odds)).filter((n) => Number.isFinite(n))))}
                </span>
                <Button size="sm" variant="secondary" type="button"
                  disabled={form.legs.length >= 6}
                  onClick={() => setLegs((legs) => [...legs, newLeg(legs.length)])}>
                  <Plus className="w-3 h-3 mr-1" />Add Leg
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {form.legs.map((leg, i) => (
                <div key={i} className="rounded-xl border border-border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Leg {i + 1}</div>
                    <Button size="sm" variant="ghost" type="button"
                      disabled={form.legs.length <= 2}
                      onClick={() => setLegs((legs) => legs.filter((_, idx) => idx !== i))}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Sport">
                      <Select value={leg.sport_id} onValueChange={(v) =>
                        setLegs((legs) => legs.map((x, idx) => idx === i ? { ...x, sport_id: v } : x))}>
                        <SelectTrigger><SelectValue placeholder="Select sport" /></SelectTrigger>
                        <SelectContent>{sports.map((s) => <SelectItem key={s.id} value={s.id}>{s.code}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="Odds (American)">
                      <Input type="number" value={leg.odds} placeholder="-110"
                        onChange={(e) => setLegs((legs) => legs.map((x, idx) => idx === i ? { ...x, odds: e.target.value } : x))} />
                    </Field>
                    <Field label="Game" full>
                      <Input value={leg.game_description} placeholder="Lakers @ Warriors"
                        onChange={(e) => setLegs((legs) => legs.map((x, idx) => idx === i ? { ...x, game_description: e.target.value } : x))} />
                    </Field>
                    <Field label="Pick" full>
                      <Input value={leg.pick_description} placeholder="Lakers ML / Curry Over 27.5 pts"
                        onChange={(e) => setLegs((legs) => legs.map((x, idx) => idx === i ? { ...x, pick_description: e.target.value } : x))} />
                    </Field>
                    <Field label="Line">
                      <Input type="number" step="0.5" value={leg.line}
                        onChange={(e) => setLegs((legs) => legs.map((x, idx) => idx === i ? { ...x, line: e.target.value } : x))} />
                    </Field>
                    <Field label="Game Start (local time)">
                      <Input type="datetime-local" value={leg.commence_time}
                        onChange={(e) => setLegs((legs) => legs.map((x, idx) => idx === i ? { ...x, commence_time: e.target.value } : x))} />
                    </Field>
                    <Field label="Reasoning" full>
                      <Textarea rows={2} value={leg.reasoning}
                        onChange={(e) => setLegs((legs) => legs.map((x, idx) => idx === i ? { ...x, reasoning: e.target.value } : x))} />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Saving…" : form.id ? "Save Changes" : "Create Parlay"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
